import ButtonLink from '@components/button-link/ButtonLink';
import Hero from '@components/hero/Hero';
import { ContentBlock } from '@layouts/ContentBlocks';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import IconAffirm from '@ui/icons/affirm/black-white-bg.svg';
import IconShield from '@ui/icons/imaterial/base/Shield with Check 01.svg';
import IconBank from '@ui/icons/imaterial/business-and-finance/Bank.svg';
import IconCalendar from '@ui/icons/imaterial/time-and-date/Calendar.svg';
import theme from '@ui/theme';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import useStyles from './affirm.style';

const AffirmPage = () => {
  const { classes } = useStyles();
  const { md } = useBreakpoints();

  return (
    <>
      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key="shop"
          backgroundColor="#ededed"
          backgroundImage="/images/hero-images/affirm-hero.jpeg"
          overlayStyle="cover"
          overlay={md ? false : true}
          style={{
            backgroundPosition: '50% 70%',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            height: md ? 460 : 300,
          }}
          contentClass={classes.heroContent}
        >
          <Box
            textAlign={md ? 'left' : 'center'}
          >
            <Typography
              variant="display1"
              component="h1"
              style={{
                color: theme.palette.common.white,
                textShadow: '0px 0px 20px rgba(0, 0, 0, .6)',
                fontSize: md ? 72 : 36,
                lineHeight: 1.15,
                fontWeight: 500,
                marginBottom: theme.spacing(1.5)
              }}
            >
              List now. Pay Later.
            </Typography>
            <Typography
              style={{
                color: theme.palette.common.white,
                textShadow: '0px 0px 20px rgba(0, 0, 0, .7)',
                marginBottom: theme.spacing(6),
                fontSize: md ? 26 : 14
              }}
            >
              Staging + services as low as $175/month when you
              <br />
              checkout with Affirm*
            </Typography>

            <Button
              size="large"
              variant="contained"
              color="secondary"
              component={ButtonLink}
              href="/home-staging/get-a-quote?step=email"
              className={classes.stagingQuoteButton}
            >
              Get an instant quote
            </Button>
          </Box>
        </Hero>
      </ContentBlock>

      <ContentBlock>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Typography
            style={{
              fontWeight: 600,
              marginBottom: md ? theme.spacing(7.5) : theme.spacing(3),
              textAlign: 'center'
            }}
            variant="h2"
            component="h3"
          >
            Finance your listing with Affirm
          </Typography>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width={md ? '60%' : '80%'}
            marginBottom={md ? theme.spacing(8) : theme.spacing(3)}
          >
            <Box className={classes.financeContentContainer}>
              <Typography className={classes.financeContentFirstRow}>
                $175
              </Typography>
              <Typography className={classes.financeContentSecondRow}>
                per month
              </Typography>
            </Box>

            <Divider
              flexItem
              orientation="vertical"
              className={classes.financeContentDivider}
            />

            <Box className={classes.financeContentContainer}>
              <Typography className={classes.financeContentFirstRow}>
                12
              </Typography>
              <Typography className={classes.financeContentSecondRow}>
                months
              </Typography>
            </Box>

            <Divider
              flexItem
              orientation="vertical"
              className={classes.financeContentDivider}
            />

            <Box className={classes.financeContentContainer}>
              <Typography className={classes.financeContentFirstRow}>
                0%
              </Typography>
              <Typography className={classes.financeContentSecondRow}>
                APR*
              </Typography>
            </Box>
          </Box>

          <Button
            size="large"
            variant="contained"
            color="secondary"
            component={ButtonLink}
            href="/home-staging/get-a-quote?step=email"
            className={classes.stagingQuoteButton}
            style={{ marginBottom: theme.spacing(3) }}
          >
            Get an instant quote
          </Button>

          <Typography
            variant="h6"
          >
            *Finance a base staging package with Affirm for as low as $175 over 12-months at 0% APR based on full price of $2100 (down payment may be required).
          </Typography>

        </Box>
      </ContentBlock>

      <Box
        style={{
          backgroundColor: '#DBDED0',
          maxWidth: 'none',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <ContentBlock >
          <Box
            textAlign="left"
          >
            <Avatar 
              src="/images/affirm/heidi-kuker.jpeg"
              style={{
                height: md ? 120 : 90,
                width: md ? 120 : 90
              }}
            />
            <div
              style={{
                overflow: 'hidden',
                position: 'relative',
                width: 60,
                height: md ? 40 : 28,
                borderRadius: theme.shape.borderRadius,
                margin: theme.spacing(4.5, 0)
              }}
            >
              <Image
                fill
                src="/images/affirm/quote.png"
                alt=""
                sizes="100vw"
                style={{
                  objectFit: 'contain'
                }}
              />
            </div>
            <Typography
              variant="h3"
              style={{
                fontWeight: 500,
                marginBottom: theme.spacing(3.5),
                lineHeight: 1.4
              }}
            >
              I love being able to buy with Affirm. Having more stability in my monthly expenses gives me confidence and freedom. I can pay off my balance at closing, or continue to reinvest in my business to win more listings with my earnings.
            </Typography>
            <Typography
              variant="h4"
              style={{
                opacity: 0.6
              }}
            >
              Heidi Kuker, Compass Real Estate, Denver
            </Typography>
          </Box>
        </ContentBlock>
      </Box>

      <ContentBlock 
        margin={[theme.spacing(4.5, 0)]}
        marginMd={[theme.spacing(6.5), 0]}
        padding={[theme.spacing(3)]}
        paddingMd={[theme.spacing(10)]}
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent={md ? 'flex-start' : 'center'}
          alignItems="baseline"
          marginBottom={md ? theme.spacing(6.5) : theme.spacing(3)}
        >
          <IconAffirm style={{ height: md ? 48 : 40, width: md ? 116 : 100 }} />
          <Typography
            style={{
              fontSize: md ? 26 : 14,
              opacity: 0.6,
              margin: theme.spacing(0, 1.5)
            }}
          >
            +
          </Typography>
          <Typography
            style={{
              fontSize: md ? 36 : 24,
              fontWeight: 600
            }}
          >
            Guest House
          </Typography>
        </Box>

        <Box
          display="flex"
          flexDirection= {md ? 'row' : 'column'}
          justifyContent="space-evenly"
          marginBottom={md ? theme.spacing(8) : theme.spacing(3)}
        >
          <Box className={classes.affirmPlusGHContainer}>
            <IconBank
              style={{
                height: md ? 70 : 40,
                width: md ? 70 : 40,
                marginBottom: md ? theme.spacing(3) : 0
              }}
            />
            <Typography className={classes.affirmPlusGHTitle}>
              Pre-qualify
            </Typography>
            <Typography className={classes.affirmPlusGHContent}>
              Complete a quick application at checkout.
            </Typography>
          </Box>

          <Box className={classes.affirmPlusGHContainer}>
            <IconShield
              style={{
                height: md ? 70 : 40,
                width: md ? 70 : 40,
                marginBottom: md ? theme.spacing(3) : 0
              }}
            />
            <Typography className={classes.affirmPlusGHTitle}>
              Easy payments
            </Typography>
            <Typography className={classes.affirmPlusGHContent}>
              Make simple monthly payments with no hidden fees.
            </Typography>
          </Box>

          <Box className={classes.affirmPlusGHContainer}>
            <IconCalendar
              style={{
                height: md ? 70 : 40,
                width: md ? 70 : 40,
                marginBottom: md ? theme.spacing(3) : 0
              }}
            />
            <Typography className={classes.affirmPlusGHTitle}>
              Choose your terms
            </Typography>
            <Typography className={classes.affirmPlusGHContent}>
              Choose a term-length from 12, 18 or 24 months.*
            </Typography>
          </Box>
        </Box>

        <Button
          size="large"
          variant="contained"
          color="secondary"
          component={ButtonLink}
          href="/home-staging/get-a-quote?step=email"
          className={classes.stagingQuoteButton}
        >
          Get an instant quote
        </Button>

        <Typography
          variant="h6"
          style={{ opacity: 0.6, marginTop: md ? theme.spacing(8) : theme.spacing(3) }}
        >
          *Stage with Guest House for as low as $175/month over 12-months at 0% APR. Based on a full purchase price of $2100. Subject to credit check and approval. Your rate will be 0-36% APR based on credit, and is subject to an eligibility check. For example, a $700 purchase might cost $63.18/mo over 12 months at 15% APR. Payment options through Affirm are provided by these lending partners: affirm.com/lenders. Options depend on your purchase amount and a down payment may be required.
        </Typography>

        <Typography style={{ marginTop: theme.spacing(3) }}>
          For more information, visit
          {' '}
          <Link
            href="https://www.affirm.com/how-it-works"
            style={{ textDecoration: 'underline', color: theme.palette.common.black }}
            target="_blank"
          >
          
            https://www.affirm.com/how-it-works
          
          </Link>
        </Typography>
      </ContentBlock>
    </>
  );
};

export default AffirmPage;
